import { useState, useEffect } from "react";
import type { SyntheticEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { adminApi } from "../api";
import { permissionsApi } from "../api/permissions";
import { Button, LoadingText, Select } from "../components/ui";
import { formatCompact } from "../utils";
import { ALL_ROLES, ROLES } from "../constants/roles";
import { useAuth } from "../hooks/useAuth";
import type { AppUser } from "../types/user";

type DailyToken = { date: string; label: string; tokens: number };
type TokenHistory = {
  today: number;
  yesterday: number;
  total: number;
  days: number;
  daily: DailyToken[];
};

const ROLES = [...ALL_ROLES].reverse();

type DataGroup = { id: string; name: string };

export default function AdminUserEditPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role?.name === ROLES.SUPER_ADMIN;

  const [user, setUser] = useState<AppUser | null>(null);
  const [loadError, setLoadError] = useState("");
  const [dataGroups, setDataGroups] = useState<DataGroup[]>([]);
  const [teams, setTeams] = useState<{ id: string; name: string }[]>([]);

  const [form, setForm] = useState({
    full_name: "",
    role_name: "viewer",
    is_active: true,
    data_group_id: "",
    slack_user_id: "",
    team_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwSaved, setPwSaved] = useState(false);

  const [twoFaResetting, setTwoFaResetting] = useState(false);
  const [twoFaReset, setTwoFaReset] = useState(false);

  const [tokenHistory, setTokenHistory] = useState<TokenHistory | null>(null);

  useEffect(() => {
    if (!userId) return;
    Promise.all([
      adminApi.getUser(userId),
      permissionsApi.getDataGroups(),
      adminApi.listTeams(),
      adminApi.getUserTokenHistory(userId),
    ]).then(([userRes, dgRes, teamsRes, tokenRes]) => {
      const u: AppUser = userRes.data;
      setUser(u);
      setForm({
        full_name: u.full_name,
        role_name: u.role?.name ?? "viewer",
        is_active: u.is_active ?? true,
        data_group_id: u.data_group?.id ?? "",
        slack_user_id: u.slack_user_id ?? "",
        team_id: u.team?.id ?? "",
      });
      setDataGroups(dgRes.data);
      setTeams(teamsRes.data);
      setTokenHistory(tokenRes.data);
    }).catch(() => {
      setLoadError("Failed to load user.");
    });
  }, [userId]);

  function set<K extends keyof typeof form>(field: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function reset2fa() {
    if (!confirm(`Reset 2FA for ${user?.full_name}? They will be able to log in with password only and must re-enrol within 7 days.`)) return;
    setTwoFaResetting(true);
    try {
      await adminApi.resetUser2fa(userId!);
      setUser((u) => u ? { ...u, totp_enabled: false } : u);
      setTwoFaReset(true);
      setTimeout(() => setTwoFaReset(false), 3000);
    } finally {
      setTwoFaResetting(false);
    }
  }

  async function submit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.updateUser(userId!, {
        ...form,
        data_group_id: form.data_group_id || null,
        slack_user_id: form.slack_user_id.trim() || null,
        team_id: form.team_id || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { return; } finally {
      setLoading(false);
    }
  }

  async function submitPassword(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setPwError("");
    if (newPassword !== confirmPassword) {
      setPwError("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setPwError("Password must be at least 8 characters");
      return;
    }
    setPwLoading(true);
    try {
      await adminApi.resetUserPassword(userId!, newPassword);
      setPwSaved(true);
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => setPwSaved(false), 2000);
    } catch { return; } finally {
      setPwLoading(false);
    }
  }

  if (loadError) {
    return (
      <div className="p-8">
        <p className="text-sm text-red-600">{loadError}</p>
        <button onClick={() => navigate("/admin?tab=Users")} className="mt-4 text-sm text-[#1D345F] hover:underline">
          ← Back to users
        </button>
      </div>
    );
  }

  if (!user) {
    return <LoadingText className="p-8" />;
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-8">
      {/* Breadcrumb */}
      <button
        onClick={() => navigate("/admin?tab=Users")}
        className="mb-6 flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#1D345F]"
      >
        <ArrowLeft size={15} />
        Back to users
      </button>

      <h1 className="mb-1 text-xl font-semibold text-gray-900">{user.full_name}</h1>
      <p className="mb-8 text-sm text-gray-500">{user.email}</p>

      {/* User info form */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6">
        <h2 className="mb-5 text-sm font-semibold text-gray-800">User details</h2>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Full name</label>
            <input
              required
              value={form.full_name}
              onChange={(e) => set("full_name", e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
            <Select value={form.role_name} onChange={(e) => set("role_name", e.target.value)} className="w-full">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Data access group</label>
            <Select value={form.data_group_id} onChange={(e) => set("data_group_id", e.target.value)} className="w-full">
              <option value="">— Unrestricted (all datasources) —</option>
              {dataGroups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </Select>
            <p className="mt-1 text-xs text-gray-400">Restricts which datasources this user can query. Leave blank for full access.</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Team</label>
            <Select value={form.team_id} onChange={(e) => set("team_id", e.target.value)} className="w-full">
              <option value="">— No team —</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Select>
            <p className="mt-1 text-xs text-gray-400">Assigning to a team will share the team's default dashboards with this user.</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Slack user ID</label>
            <input
              value={form.slack_user_id}
              onChange={(e) => set("slack_user_id", e.target.value)}
              placeholder="e.g. U0123456789"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-400">
              Links this account to a Slack user for DM bot access. Find the ID in Slack via the user's profile menu.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={form.is_active}
              onChange={(e) => set("is_active", e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">Active</label>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={loading}>
              {saved ? "Saved!" : loading ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </div>

      {/* Token usage */}
      {tokenHistory && (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6">
          <h2 className="mb-5 text-sm font-semibold text-gray-800">Token usage</h2>
          <div className="mb-6 grid grid-cols-3 gap-4">
            {[
              { label: "Today", value: tokenHistory.today },
              { label: "Yesterday", value: tokenHistory.yesterday },
              { label: `Last ${tokenHistory.days} days`, value: tokenHistory.total },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                <p className="text-xs text-gray-400">{label}</p>
                <p className="mt-1 text-lg font-semibold text-gray-900 tabular-nums">
                  {value === 0 ? <span className="text-gray-300">—</span> : formatCompact(value)}
                </p>
              </div>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={tokenHistory.daily} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
                interval={Math.floor(tokenHistory.daily.length / 6)}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => formatCompact(v)}
                width={40}
              />
              <Tooltip
                formatter={(v: number) => [v.toLocaleString(), "Tokens"]}
                labelStyle={{ fontSize: 12 }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
              />
              <Bar dataKey="tokens" fill="#1D345F" radius={[3, 3, 0, 0]} maxBarSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* 2FA reset — super admin only */}
      {isSuperAdmin && (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6">
          <h2 className="mb-1 text-sm font-semibold text-gray-800">Two-factor authentication</h2>
          <p className="mb-4 text-xs text-gray-500">
            Status: {user.totp_enabled
              ? <span className="font-medium text-green-600">Enabled</span>
              : <span className="font-medium text-gray-400">Not enabled</span>}
          </p>
          {user.totp_enabled ? (
            <>
              <p className="mb-4 text-sm text-gray-600">
                Resetting 2FA will immediately disable it and open a 7-day window for the user to re-enrol. Use this if they have lost access to their authenticator app.
              </p>
              <Button variant="danger" onClick={reset2fa} disabled={twoFaResetting}>
                {twoFaReset ? "2FA reset — user can now log in" : twoFaResetting ? "Resetting…" : "Reset 2FA"}
              </Button>
            </>
          ) : (
            <p className="text-sm text-gray-400">No action needed — 2FA is not currently active for this user.</p>
          )}
        </div>
      )}

      {/* Password reset */}
      <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6">
        <h2 className="mb-5 text-sm font-semibold text-gray-800">Reset password</h2>
        <form onSubmit={submitPassword} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">New password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Confirm new password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          {pwError && <p className="text-sm text-red-600">{pwError}</p>}
          <div className="flex justify-end">
            <Button type="submit" disabled={pwLoading}>
              {pwSaved ? "Reset!" : pwLoading ? "Saving…" : "Reset password"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
